import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import { startConversation } from '../../utils/startConversation';
import ZoomableImg from '../../components/ZoomableImg';
import { startConversationWithUnion } from '../../utils/startConversationWithUnion';
import { resolveUnionId } from '../../utils/resolveUnionId';
import LanguageToggle from '../../components/LanguageToggle';
import { extractYouTubeId, extractVimeoId } from '../../utils/videoPortfolio';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import LineIcon from '../../components/LineIcon';


const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GREEN     = '#1D9E75';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>
        {label}
      </p>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', minWidth: '160px' }}>{label}</span>
      <span style={{ fontSize: '0.875rem', color: WHITE, fontWeight: 500, textAlign: 'right', flex: 1 }}>{value}</span>
    </div>
  );
}

export default function WorkerProfile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { workerId } = useParams();
  const [searchParams] = useSearchParams();

  const [worker, setWorker]               = useState(null);
  const [profile, setProfile]             = useState(null);
  const [trades, setTrades]               = useState(null);
  const [experience, setExperience]       = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [licenses, setLicenses]           = useState([]);
  const [portfolio, setPortfolio]         = useState([]);
  const [loading, setLoading]             = useState(true);

  // Who's actually looking at this profile -- determines where "back" goes and which
  // conversation-starter to use. Never trust a hardcoded destination here: this page is
  // shared between the employer and union dashboards, and each must only ever return to
  // its own dashboard, regardless of which one the viewer came from or visited earlier.
  const [viewerType, setViewerType] = useState(null); // 'employer' | 'union' | null
  const [viewerId, setViewerId]     = useState(null);
  // The real employer entity this viewer acts as -- distinct from viewerId whenever the
  // viewer is an invited employer_staff seat rather than the original employer signup.
  const [viewerEmployerId, setViewerEmployerId] = useState(null);
  // Same distinction on the union side: an invited union_staff seat acts on the owner's union_id.
  const [viewerUnionId, setViewerUnionId] = useState(null);

  useEffect(() => {
    const detectViewer = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      setViewerId(session.user.id);
      const [{ data: staffRow }, unionId] = await Promise.all([
        // is_active filtered for the same reason as resolveEmployerId -- this grants
        // viewerType 'employer', so a deactivated seat must not qualify.
        supabase.from('employer_staff').select('employer_id').eq('user_id', session.user.id).eq('is_active', true).maybeSingle(),
        resolveUnionId(supabase, session.user.id),
      ]);
      if (staffRow) { setViewerType('employer'); setViewerEmployerId(staffRow.employer_id); }
      else if (unionId) { setViewerType('union'); setViewerUnionId(unionId); }
    };
    detectViewer();
  }, []);

  const backPath = viewerType === 'union' ? '/union/dashboard' : '/employer/dashboard';
  const goBackToDashboard = () => {
    if (viewerType) navigate(backPath, { state: { activeTab: 'Candidates' } });
    else navigate(-1); // viewer role not resolved yet/unknown -- don't guess, just go back
  };

  useEffect(() => {
    const loadProfile = async () => {
      const [workerRes, profileRes, tradesRes, expRes, certsRes, licensesRes, portfolioRes] = await Promise.all([
        supabase.from('workers').select('*, resume_url').eq('id', workerId).single(),
        supabase.from('worker_profiles').select('*').eq('id', workerId).single(),
        supabase.from('worker_trades').select('*').eq('worker_id', workerId).single(),
        supabase.from('worker_experience').select('*').eq('worker_id', workerId).order('start_year', { ascending: false }),
        supabase.from('worker_certifications').select('*').eq('worker_id', workerId),
        supabase.from('worker_licenses').select('*').eq('worker_id', workerId),
        supabase.from('worker_portfolio').select('*').eq('worker_id', workerId).order('created_at', { ascending: false }),
      ]);
      setWorker(workerRes.data);
      setProfile(profileRes.data);
      setTrades(tradesRes.data);
      setExperience(expRes.data || []);
      setCertifications(certsRes.data || []);
      setLicenses(licensesRes.data || []);
      setPortfolio(portfolioRes.data || []);
      setLoading(false);
    };
    loadProfile();
  }, [workerId]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${NAVY}; font-family: ${FONT_BODY}; }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  if (loading) return (
    <div style={{ background: NAVY, height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: AMBER, fontFamily: 'sans-serif', fontSize: '16px' }}>{t('worker.profileView.loading')}</div>
    </div>
  );

  if (!worker) return (
    <div style={{ background: NAVY, height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: WHITE, fontFamily: 'sans-serif', fontSize: '16px' }}>{t('worker.profileView.notFound')}</div>
    </div>
  );

  const initials    = `${worker.first_name?.[0] || ''}${worker.last_name?.[0] || ''}`;
  const fullName    = `${worker.first_name || ''} ${worker.last_name || ''}`.trim();
  const availColor  = (trades?.availability === 'Full-time' || trades?.availability === 'Open to all') ? GREEN : AMBER;
  const availLabel  = trades?.availability || '';

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh' }}>

      {/* Navbar */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200, height: '60px',
        backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 2rem',
      }}>
        <div onClick={goBackToDashboard} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
          <span
            onClick={goBackToDashboard}
            style={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', cursor: 'pointer' }}
          >
            {t('worker.profileView.backToCandidates')}
          </span>
          <LanguageToggle dark />
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '2rem 1.25rem 3rem' }}>

        {/* ── Header card ── */}
        <div style={{ backgroundColor: NAVY_MID, borderRadius: '16px', padding: '28px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
            {/* Avatar */}
            {profile?.profile_photo_url ? (
              <div style={{ width: '72px', height: '72px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                <ZoomableImg src={profile.profile_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            ) : (
              <div style={{
                width: '72px', height: '72px', borderRadius: '50%',
                backgroundColor: AMBER, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontWeight: 700, fontSize: '1.6rem', color: NAVY, fontFamily: FONT_BODY }}>{initials}</span>
              </div>
            )}
            {/* Info */}
            <div style={{ flex: 1 }}>
              <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '4px' }}>
                {fullName}
              </h1>
              <p style={{ fontSize: '0.9rem', color: AMBER, fontWeight: 600, marginBottom: '6px' }}>
                {trades?.trade || t('worker.profileView.tradeWorkerFallback')}
              </p>
              {(profile?.city || profile?.state) && (
                <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', marginBottom: '10px' }}>
                  {[profile?.city, profile?.state].filter(Boolean).join(', ')}
                </p>
              )}
              {availLabel && (
                <span style={{
                  display: 'inline-block',
                  backgroundColor: availColor === GREEN ? 'rgba(29,158,117,0.15)' : 'rgba(245,166,35,0.15)',
                  border: `1px solid ${availColor}`,
                  borderRadius: '999px', padding: '3px 14px',
                  fontSize: '0.75rem', fontWeight: 700, color: availColor,
                }}>
                  {availLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Resume ── */}
        {worker.resume_url && (
          <div style={{ backgroundColor: NAVY_MID, borderRadius: '16px', padding: '18px 24px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
              {t('worker.profileView.hasResume')}
            </p>
            <button
              onClick={() => window.open(worker.resume_url, '_blank')}
              style={{
                background: AMBER, border: 'none', color: NAVY,
                borderRadius: '8px', padding: '10px 20px',
                fontWeight: 700, fontSize: '0.88rem',
                cursor: 'pointer', fontFamily: FONT_BODY,
                display: 'flex', alignItems: 'center', gap: '7px',
                flexShrink: 0,
              }}
            >
              <LineIcon name="document" />
              {t('worker.profileView.downloadResume')}
            </button>
          </div>
        )}

        {/* ── Detail card ── */}
        <div style={{ backgroundColor: NAVY_MID, borderRadius: '16px', padding: '28px', marginBottom: '16px' }}>

          {/* About */}
          {profile?.bio && (
            <Section label={t('worker.profileView.sectionAbout')}>
              <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}>{profile.bio}</p>
            </Section>
          )}

          {/* Trade and experience */}
          <Section label={t('worker.profileView.sectionTradeExperience')}>
            <InfoRow label={t('worker.profileView.tradeLabel')}                     value={trades?.trade} />
            <InfoRow label={t('worker.signup.step5.experienceLevelLabel')}          value={trades?.experience_level} />
            <InfoRow label={t('worker.signup.step5.yearsExperienceLabel')}          value={trades?.years_experience} />
            <InfoRow label={t('worker.signup.step3.minRateLabel')}                  value={trades?.min_hourly_rate ? `$${trades.min_hourly_rate}/hr` : null} />
            <InfoRow label={t('worker.signup.step3.targetRateLabel')}               value={trades?.target_hourly_rate ? `$${trades.target_hourly_rate}/hr` : null} />
            <InfoRow label={t('worker.signup.step5.unionInterestLabel')}            value={trades?.union_interest} />
          </Section>

          {/* Work Experience */}
          {experience.length > 0 && (
            <Section label={t('worker.profileView.sectionWorkExperience')}>
              {experience.map((exp, i) => (
                <div key={i} style={{ paddingBottom: '14px', marginBottom: '14px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: exp.description ? '6px' : 0 }}>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: WHITE, fontWeight: 700 }}>{exp.company_name}</span>
                      {exp.role?.trim() && (
                        <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.75)', fontWeight: 700 }}>{exp.company_name?.trim() ? ' · ' : ''}{exp.role}</span>
                      )}
                    </div>
                    <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {exp.start_year}–{exp.current ? t('worker.profileView.present') : exp.end_year}
                    </span>
                  </div>
                  {exp.description && (
                    <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, margin: 0 }}>{exp.description}</p>
                  )}
                </div>
              ))}
            </Section>
          )}

          {/* Certifications */}
          {certifications.length > 0 && (
            <Section label={t('worker.profileView.sectionCertifications')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {certifications.map((cert, i) => (
                  <div key={i} style={{ backgroundColor: 'rgba(245,166,35,0.07)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: '10px', padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: cert.issuing_organization || cert.expiry_date ? '4px' : 0 }}>
                      <span style={{ fontSize: '0.875rem', color: WHITE, fontWeight: 700 }}>
                        {cert.certification_name}
                      </span>
                      {cert.verification_status === 'verified' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(29,158,117,0.15)', color: '#1D9E75', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', flexShrink: 0 }}>
                          <LineIcon name="check" size={16} />
                          {t('worker.profileView.verified')}
                        </span>
                      )}
                    </div>
                    {cert.issuing_organization && (
                      <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: cert.expiry_date ? '2px' : 0 }}>
                        {cert.issuing_organization}
                      </span>
                    )}
                    {cert.expiry_date && (
                      <span style={{ fontSize: '0.75rem', color: AMBER, fontWeight: 600 }}>
                        {t('worker.signup.step5.expires', { date: new Date(cert.expiry_date).toLocaleDateString() })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Licenses */}
          {licenses.length > 0 && (
            <Section label={t('worker.profileView.sectionLicenses')}>
              {licenses.map((lic, i) => (
                <div key={i} style={{ paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    <div>
                      <span style={{ fontSize: '0.875rem', color: WHITE, fontWeight: 700, display: 'block', marginBottom: '3px' }}>
                        {lic.license_name}
                      </span>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {lic.license_number && (
                          <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>#{lic.license_number}</span>
                        )}
                        {lic.state && (
                          <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>{lic.state}</span>
                        )}
                      </div>
                    </div>
                    {lic.expiry_date && (
                      <span style={{ fontSize: '0.75rem', color: AMBER, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {t('worker.signup.step5.expires', { date: new Date(lic.expiry_date).toLocaleDateString() })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {/* Portfolio */}
          {portfolio.length > 0 && (
            <Section label={t('worker.profileView.sectionPortfolio')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {portfolio.map((item, i) => {
                  const ytId    = item.video_type === 'youtube' ? extractYouTubeId(item.video_url || '') : null;
                  const vimeoId = item.video_type === 'vimeo'   ? extractVimeoId(item.video_url || '')   : null;
                  return (
                    <div key={i} style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
                      {ytId && (
                        <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                          <iframe src={`https://www.youtube.com/embed/${ytId}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={item.title} />
                        </div>
                      )}
                      {vimeoId && (
                        <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                          <iframe src={`https://player.vimeo.com/video/${vimeoId}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title={item.title} />
                        </div>
                      )}
                      {item.video_type === 'upload' && item.video_url && (
                        <video controls style={{ width: '100%', display: 'block', maxHeight: '280px', background: '#000' }} src={item.video_url} />
                      )}
                      <div style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: '0.875rem', color: WHITE, fontWeight: 700, display: 'block', marginBottom: item.description ? '4px' : 0 }}>{item.title}</span>
                        {item.description && <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, display: 'block' }}>{item.description}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Additional info */}
          <Section label={t('worker.profileView.sectionAdditionalInfo')}>
            <InfoRow label={t('worker.signup.step5.veteranStatusLabel')}   value={profile?.veteran_status} />
            <InfoRow label={t('worker.profileView.giBillEligibleLabel')}   value={profile?.gi_bill_eligible != null ? (profile.gi_bill_eligible ? t('common.yes') : t('common.no')) : null} />
            <InfoRow label={t('worker.profileView.secondChanceLabel')}     value={profile?.second_chance != null ? (profile.second_chance ? t('common.yes') : t('common.no')) : null} />
          </Section>

        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            onClick={goBackToDashboard}
            style={{
              background: 'transparent', border: '1px solid rgba(255,255,255,0.25)',
              color: 'rgba(255,255,255,0.7)', borderRadius: '8px',
              padding: '12px 24px', fontWeight: 600, fontSize: '0.9rem',
              cursor: 'pointer', fontFamily: FONT_BODY,
            }}
          >
            {t('worker.profileView.backToCandidates')}
          </button>
          <button
            onClick={async () => {
              if (!viewerId) return;
              const convId = viewerType === 'union'
                ? await startConversationWithUnion(supabase, workerId, viewerUnionId, searchParams.get('apprenticeshipId'), 'union')
                : await startConversation(supabase, workerId, viewerEmployerId, searchParams.get('jobId'), 'employer');
              if (!convId) return;
              navigate(backPath, { state: { tab: 'Messages', conversationId: convId } });
            }}
            style={{
              background: AMBER, border: 'none', color: NAVY,
              borderRadius: '8px', padding: '12px 28px',
              fontWeight: 700, fontSize: '0.9rem',
              cursor: 'pointer', fontFamily: FONT_BODY,
            }}
          >
            {t('worker.profileView.messageWorker')}
          </button>
        </div>

      </div>
    </div>
  );
}
